import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { analytics } from '@/lib/analytics';

/**
 * React hook for easy analytics integration in components
 * Automatically includes user context where applicable
 */
export function useAnalytics() {
  const location = useLocation();
  const { user, isAdmin } = useAuth();

  // Determine user type for tracking
  const getUserType = useCallback((): 'guest' | 'member' | 'admin' => {
    if (!user) return 'guest';
    if (isAdmin) return 'admin';
    return 'member';
  }, [user, isAdmin]);

  // Track page view with current context
  const trackPageView = useCallback((pageName: string) => {
    analytics.pageView(pageName, location.pathname, getUserType());
  }, [location.pathname, getUserType]);

  // Track navigation from current page
  const trackNavigation = useCallback((
    to: string,
    method: 'menu' | 'link' | 'command_palette' | 'mobile_menu' | 'dropdown'
  ) => {
    analytics.navigation(location.pathname, to, method);
  }, [location.pathname]);

  // Track button click with page context
  const trackButtonClick = useCallback((
    buttonName: string,
    additionalContext?: string
  ) => {
    analytics.buttonClick(
      buttonName,
      additionalContext || location.pathname
    );
  }, [location.pathname]);

  // Track feature engagement
  const trackFeatureEngagement = useCallback((
    featureName: string,
    action: 'open' | 'close' | 'interact' | 'complete'
  ) => {
    analytics.featureEngagement(featureName, action);
  }, []);

  // Track CTA click with location context
  const trackCTAClick = useCallback((
    ctaName: string,
    destination?: string
  ) => {
    analytics.ctaClick(ctaName, location.pathname, destination);
  }, [location.pathname]);

  return {
    // Direct access to all analytics functions
    ...analytics,
    // Context-aware helpers
    trackPageView,
    trackNavigation,
    trackButtonClick,
    trackFeatureEngagement,
    trackCTAClick,
    // User type getter
    getUserType,
  };
}
